import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRues } from "../hooks/useRues";

import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import Select from "react-select";

// ICONOS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import axios from "axios";

// 🔥 OPCIONES

const opcionesSector = [
  { value: "Privado", label: "Privado" },
  { value: "Público", label: "Público" },
  { value: "Mixto", label: "Mixto" },
];

const opcionesAfiliacion = [
  { value: "Afiliado", label: "Afiliado" },
  { value: "No Afiliado", label: "No Afiliado" },
];

const opcionesRegional = [
  { value: "ANTIOQUIA", label: "ANTIOQUIA" },
  { value: "CARIBE", label: "CARIBE" },
  { value: "CENTRO", label: "CENTRO" },
  { value: "ORIENTE", label: "ORIENTE" },
  { value: "SUROCCIDENTE", label: "SUROCCIDENTE" },
];

export default function BuscarRues() {
  const navigate = useNavigate();

  const { consultarRues, obtenerValor, eliminarAfiliado, actualizarAfiliado } =
    useRues();

  const [nit, setNit] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [todasColumnas, setTodasColumnas] = useState([]);
  const [columnasExtras, setColumnasExtras] = useState([]);

  const [openValor, setOpenValor] = useState(false);
  const [valorData, setValorData] = useState(null);

  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [opcionesCategoria, setOpcionesCategoria] = useState([]);
  const [opcionesEmpresa, setOpcionesEmpresa] = useState([]);

  useEffect(() => {
    const obtenerCategorias = async () => {
      try {
        const res = await axios.get(
          "https://ruesicn.azurewebsites.net/api/ObtenerCategoria",
        );

        const opciones = res.data.map((item) => ({
          value: item.categoria,
          label: item.categoria,
        }));

        setOpcionesCategoria(opciones);
      } catch (error) {
        console.error("Error cargando categorías", error);
      }
    };

    obtenerCategorias();
  }, []);
  useEffect(() => {
    const obtenerTamanoEmpresa = async () => {
      try {
        const res = await axios.get(
          "https://ruesicn.azurewebsites.net/api/ObtenerTamanoEmpresa",
        );

        const opciones = res.data.map((item) => ({
          value: item.tipoEmpresa, // 🔥 ajusta si viene otro nombre
          label: item.tipoEmpresa,
        }));

        setOpcionesEmpresa(opciones);
      } catch (error) {
        console.error("Error cargando tamaños de empresa", error);
      }
    };

    obtenerTamanoEmpresa();
  }, []);
  const columnasPrincipales = [
    "numero_identificacion",
    "razon_social",
    "municipio",
    "actividad_economica",
    "telefono",
  ];

  // 🔥 VALIDACIÓN NIT
  const validarFormatoNIT = (nit) => {
    const regex = /^\d+-\d$/;
    if (!regex.test(nit)) {
      return "Debe ingresar el NIT con formato 123456789-0";
    }
    return null;
  };

  // 🔥 ERROR HANDLER
  const getErrorMessage = (e) => {
    if (e.response) {
      return (
        e.response.data?.message ||
        e.response.data?.error ||
        `Error ${e.response.status}`
      );
    }
    return e.message;
  };

  // 🔥 COLUMNAS DINÁMICAS
  const generarColumnas = () => {
    const visibles = [...columnasPrincipales, ...columnasExtras];

    const columnas = visibles.map((key) => ({
      field: key,
      headerName: key
        .replaceAll("_", " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      flex: 1,
    }));

    columnas.push({
      field: "acciones",
      headerName: "Acciones",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const row = params.row._original;

        return (
          <Box>
            <Tooltip title="Ver valor">
              <IconButton onClick={() => handleValorFila(row)} color="info">
                <VisibilityIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Editar">
              <IconButton onClick={() => handleEditarFila(row)} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar">
              <IconButton onClick={() => handleEliminarFila(row)} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    });

    return columnas;
  };

  // 🔍 BUSCAR
  const handleBuscar = async () => {
    try {
      if (!nit) return Swal.fire("Error", "Ingrese un NIT", "error");

      const errorFormato = validarFormatoNIT(nit);
      if (errorFormato)
        return Swal.fire("Formato inválido", errorFormato, "warning");

      setLoading(true);

      const data = await consultarRues(nit);

      const row = {
        id: data.numero_identificacion,
        ...data,
        _original: data,
      };

      setRows([row]);
      setTodasColumnas(Object.keys(data));
    } catch (e) {
      Swal.fire("Error", getErrorMessage(e), "error");
    } finally {
      setLoading(false);
    }
  };

  // 👁️ VER VALOR
  const handleValorFila = async (row) => {
    try {
      const res = await obtenerValor(row.numero_identificacion);

      if (!res.valor) {
        return Swal.fire(
          "Sin valor",
          res?.message || "No hay valor disponible",
          "warning",
        );
      }

      setValorData({ id: row.numero_identificacion, ...res });
      setOpenValor(true);
    } catch (e) {
      Swal.fire("Error", getErrorMessage(e), "error");
    }
  };

  // ✏️ EDITAR (MAPEO SELECT)
  const handleEditarFila = (row) => {
    const data = { ...row };
    data.categoria_para_facturar = opcionesCategoria.find(
      (o) => o.label === row.categoria_para_facturar,
    );

    data.desc_tamano_empresa = opcionesEmpresa.find(
      (o) => o.value === row.desc_tamano_empresa,
    );

    data.tipo_de_sector = opcionesSector.find(
      (o) => o.value === row.tipo_de_sector,
    );

    data.tipo_de_afiliacion = opcionesAfiliacion.find(
      (o) => o.value === row.tipo_de_afiliacion,
    );

    data.regional = opcionesRegional.find((o) => o.value === row.regional);

    setEditData(data);
    setOpenEdit(true);
  };

  // 🗑️ ELIMINAR
  const handleEliminarFila = async (row) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar?",
      icon: "warning",
      showCancelButton: true,
    });

    if (confirm.isConfirmed) {
      try {
        await eliminarAfiliado(row.numero_identificacion);

        setRows((prev) =>
          prev.filter((r) => r.id !== row.numero_identificacion),
        );

        Swal.fire("Eliminado", "", "success");
      } catch (e) {
        Swal.fire("Error", getErrorMessage(e), "error");
      }
    }
  };

  // 💾 GUARDAR
  const handleGuardar = async () => {
    try {
      const payload = {
        ...editData,
        desc_tamano_empresa: editData.desc_tamano_empresa?.value,
        tipo_de_sector: editData.tipo_de_sector?.value,
        tipo_de_afiliacion: editData.tipo_de_afiliacion?.value,
        regional: editData.regional?.value,
        categoria_para_facturar:
          editData.categoria_para_facturar?.label ||
          editData.categoria_para_facturar,
      };

      await actualizarAfiliado(payload);

      setRows((prev) =>
        prev.map((r) =>
          r.id === payload.numero_identificacion
            ? {
                id: payload.numero_identificacion,
                ...payload,
                _original: payload,
              }
            : r,
        ),
      );

      setOpenEdit(false);
      Swal.fire("Actualizado", "", "success");
    } catch (e) {
      Swal.fire("Error", getErrorMessage(e), "error");
    }
  };

  return (
    <Card sx={{ p: 3, borderRadius: 3 }}>
      <CardContent>
        {/* VOLVER */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          Volver
        </Button>

        <Typography variant="h5">Consulta RUES</Typography>

        {/* BUSCADOR */}
        <Box sx={{ display: "flex", gap: 2, my: 2 }}>
          <TextField
            label="NIT"
            fullWidth
            value={nit}
            onChange={(e) => setNit(e.target.value)}
          />

          <Button variant="contained" onClick={handleBuscar} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Buscar"}
          </Button>
        </Box>

        {/* COLUMNAS */}
        {todasColumnas.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Columnas adicionales:</Typography>

            {todasColumnas
              .filter((col) => !columnasPrincipales.includes(col))
              .map((col) => (
                <FormControlLabel
                  key={col}
                  control={
                    <Checkbox
                      checked={columnasExtras.includes(col)}
                      onChange={(e) =>
                        e.target.checked
                          ? setColumnasExtras([...columnasExtras, col])
                          : setColumnasExtras(
                              columnasExtras.filter((c) => c !== col),
                            )
                      }
                    />
                  }
                  label={col}
                />
              ))}
          </Box>
        )}

        {/* TABLA */}
        <div style={{ height: 400 }}>
          <DataGrid rows={rows} columns={generarColumnas()} pageSize={5} />
        </div>

        {/* MODAL EDITAR */}
        <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth>
          <DialogTitle>Editar Afiliado</DialogTitle>

          <DialogContent dividers>
            {editData && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 2,
                  mt: 1,
                }}
              >
                {/* 🔹 NIT */}
                <TextField
                  fullWidth
                  size="small"
                  label="NIT"
                  value={editData.numero_identificacion || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      numero_identificacion: e.target.value,
                    })
                  }
                />

                {/* 🔹 FECHA AFILIACION */}
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha Afiliación"
                  InputLabelProps={{ shrink: true }}
                  value={
                    editData.fecha_afiliacion
                      ? editData.fecha_afiliacion.split("T")[0]
                      : ""
                  }
                  inputProps={{
                    max: new Date().toISOString().split("T")[0],
                  }}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      fecha_afiliacion: e.target.value,
                    })
                  }
                />

                {/* 🔹 RAZON SOCIAL */}
                <TextField
                  fullWidth
                  size="small"
                  label="Razón Social"
                  value={editData.razon_social || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      razon_social: e.target.value,
                    })
                  }
                />

                {/* 🔹 REGIONAL */}
                <Box>
                  <Typography variant="caption">Regional</Typography>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={opcionesRegional}
                    value={editData.regional}
                    onChange={(v) => setEditData({ ...editData, regional: v })}
                  />
                </Box>

                {/* 🔹 MUNICIPIO */}
                <TextField
                  fullWidth
                  size="small"
                  label="Municipio"
                  value={editData.municipio || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      municipio: e.target.value,
                    })
                  }
                />

                {/* 🔹 DEPARTAMENTO */}
                <TextField
                  fullWidth
                  size="small"
                  label="Departamento"
                  value={editData.departamento || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      departamento: e.target.value,
                    })
                  }
                />

                {/* 🔹 TELEFONO */}
                <TextField
                  fullWidth
                  size="small"
                  label="Teléfono"
                  value={editData.telefono || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      telefono: e.target.value,
                    })
                  }
                />

                {/* 🔹 ACTIVIDAD ECONOMICA */}
                <TextField
                  fullWidth
                  size="small"
                  label="Actividad Económica"
                  value={editData.actividad_economica || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      actividad_economica: e.target.value,
                    })
                  }
                />

                {/* 🔹 SECTOR */}
                <Box>
                  <Typography variant="caption">Sector</Typography>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={opcionesSector}
                    value={editData.tipo_de_sector}
                    onChange={(v) =>
                      setEditData({ ...editData, tipo_de_sector: v })
                    }
                  />
                </Box>

                {/* 🔹 AFILIACION */}
                <Box>
                  <Typography variant="caption">Tipo Afiliación</Typography>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={opcionesAfiliacion}
                    value={editData.tipo_de_afiliacion}
                    onChange={(v) =>
                      setEditData({ ...editData, tipo_de_afiliacion: v })
                    }
                  />
                </Box>

                {/* 🔹 CATEGORIA (SI YA LA TIENES EN editData) */}
                {editData.categoria_para_facturar && (
                  <Box>
                    <Typography variant="caption">Categoría</Typography>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      options={opcionesCategoria}
                      value={editData.categoria_para_facturar}
                      onChange={(v) =>
                        setEditData({ ...editData, categoria_para_facturar: v })
                      }
                    />
                  </Box>
                )}

                {/* 🔹 TAMAÑO EMPRESA */}
                <Box>
                  <Typography variant="caption">Tamaño Empresa</Typography>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    options={opcionesEmpresa}
                    value={editData.desc_tamano_empresa}
                    onChange={(v) =>
                      setEditData({ ...editData, desc_tamano_empresa: v })
                    }
                  />
                </Box>

                {/* 🔹 DIRECCION */}
                <TextField
                  fullWidth
                  size="small"
                  label="Dirección Comercial"
                  value={editData.direccion_comercial || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      direccion_comercial: e.target.value,
                    })
                  }
                />

                {/* 🔹 REPRESENTANTE */}
                <TextField
                  fullWidth
                  size="small"
                  label="Representante Legal"
                  value={editData.rep_legal || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      rep_legal: e.target.value,
                    })
                  }
                />

                {/* 🔹 CORREO REP */}
                <TextField
                  fullWidth
                  size="small"
                  label="Correo Representante"
                  value={editData.correo_rep_legal || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      correo_rep_legal: e.target.value,
                    })
                  }
                />

                {/* 🔹 CALIDAD */}
                <TextField
                  fullWidth
                  size="small"
                  label="Encargado Calidad"
                  value={editData.encargado_calidad || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      encargado_calidad: e.target.value,
                    })
                  }
                />

                {/* 🔹 CORREO CALIDAD */}
                <TextField
                  fullWidth
                  size="small"
                  label="Correo Calidad"
                  value={editData.correo_calidad || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      correo_calidad: e.target.value,
                    })
                  }
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleGuardar}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        {/* 👁️ MODAL VER VALOR */}
        <Dialog open={openValor} onClose={() => setOpenValor(false)} fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOnIcon color="success" />
              Valor Afiliado
            </Box>
          </DialogTitle>

          <DialogContent dividers>
            {valorData ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 2,
                }}
              >
                <TextField
                  label="NIT"
                  value={valorData.id || ""}
                  fullWidth
                  size="small"
                  disabled
                />

                <TextField
                  label="Valor"
                  value={valorData.valor || ""}
                  fullWidth
                  size="small"
                  disabled
                />

                {/* 🔥 si vienen más campos del API los pintas dinámicos */}
                {Object.keys(valorData)
                  .filter((k) => k !== "id" && k !== "valor")
                  .map((k) => (
                    <TextField
                      key={k}
                      label={k.replaceAll("_", " ").toUpperCase()}
                      value={valorData[k] || ""}
                      fullWidth
                      size="small"
                      disabled
                    />
                  ))}
              </Box>
            ) : (
              <Typography>No hay información</Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenValor(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
